export function getCompetenciesFromLosysIds(
  losysIds: number[],
  includeParents: boolean,
  competencies: Object[],
): Object[] {
  let competencyIds = []

  // Builds the competencies tree, then flattens it, to get the correct order
  const competenciesTree = buildTree(competencies)
  competencies = flattenTree(competenciesTree)

  // Filters out the competencies that match the losysIds
  const competenciesInput = losysIds
    ?.map((x) => competencies.filter((y) => y.losysIds.includes(String(x)))[0])
    .filter((comp) => comp !== undefined)

  // Adds the filtered competencies, and its parents if required
  competenciesInput.forEach((comp) => {
    if (comp) {
      if (includeParents) {
        comp.parent.forEach((parent) => {
          if (parent?.parent?.length > 0) {
            parent.parent.forEach((grandPparent) => {
              competencyIds.push(grandPparent.id)
            })
          }
          competencyIds.push(parent.id)
        })
      }
      competencyIds.push(comp.id)
    }
  })

  const competencyIdFiltered = [...new Set(competencyIds)]

  return competencies.filter((comp) => competencyIdFiltered.includes(comp.id))
}

export function getLosysCategoriesByNid(
  nid: string,
  competencies: Object[],
): string[] {
  return competencies.filter(
    (competency) => competency?.detailPage?.id == nid,
  )[0]?.losysIds
}

// Build hierarchy as before
function buildTree(items, parentId = null) {
  return items
    .filter((item) =>
      parentId
        ? item.parent.some((p) => p.id === parentId)
        : item.parent.length === 0,
    )
    .sort((a, b) => a.weight - b.weight)
    .map((item) => ({
      ...item,
      children: buildTree(items, item.id),
    }))
}

// Flatten hierarchy as before
function flattenTree(tree, level = 0) {
  return tree.reduce((acc, item) => {
    acc.push({ ...item, level })
    if (item.children.length > 0) {
      acc = acc.concat(flattenTree(item.children, level + 1))
    }
    return acc
  }, [])
}

// Merge losysIds from children to parent (for selection)
function mergeLosysIds(competenciesTree: any[]): any[] {
  const newTree = JSON.parse(JSON.stringify(competenciesTree))

  function mergeChildIds(node) {
    node.children?.forEach((child) => {
      mergeChildIds(child)
      node.losysIds = [...new Set([...node.losysIds, ...child.losysIds])]
    })
  }

  newTree.forEach((root) => mergeChildIds(root))
  return newTree
}

function removeEmptyLosysIds(tree: any[]): any[] {
  return tree.reduce((acc, node) => {
    const newNode = {
      ...node,
      children: removeEmptyLosysIds(node.children || []),
    }
    if (newNode.losysIds?.length || newNode.children?.length) {
      acc.push(newNode)
    }
    return acc
  }, [])
}

export function getLosysCategoryFilter(competencies: Object[]): Object[] {
  const competenciesTree = buildTree(competencies)
  const prunedLosysTree = removeEmptyLosysIds(competenciesTree)
  const mergedLosysTree = mergeLosysIds(prunedLosysTree)
  const competenciesFlatTree = flattenTree(mergedLosysTree)

  return competenciesFlatTree?.map((x) => {
    return {
      value: x.losysIds.length > 0 ? x.losysIds.join(',') : '1',
      text: x.name,
      level: x.level,
    }
  })
}

// Finding props utils (for client side filtering)
export const KUMA_CATEGORIES_IDS = [
  291, 398, 399, 400, 401, 402, 403, 404, 405, 406, 407, 408,
]
export const KUMA_COMPANIES_IDS = [
  291, // Kummler+Matter EVT AG, Zürich
]
export const EQUANS_COMPANIES_IDS = [
  221, // Equans Services AG, Bern
  222, // Equans Switzerland Facility Management AG, Schlieren
  223, // Equans Switzerland AG, Aarau Rohr
  224, // Equans Switzerland AG, Chur
  // 225, // Equans Switzerland AG, Bern // 403
  226, // Equans Switzerland AG, Zürich
  273, // Equans Switzerland AG, Buchs
  274, // Equans Switzerland AG, Davos Platz
  276, // Equans Switzerland AG, St. Moritz
  277, // Equans Switzerland AG, Ilanz
  278, // Equans Switzerland AG, St. Gallen
  279, // Equans Services AG, Basel
  289, // Prilly
]

export const APPROVAL_BY_MARKETING_IDS = [
  761, 763, 765, 767, 769, 771, 773, 775, 777, 779, 781, 809, 895,
]

export const IS_HIGHLIGHT_IDS = [
  760, 762, 764, 766, 768, 770, 772, 774, 776, 778, 780, 808, 894,
]

export const ADVANTAGE_IDS = [
  791, 792, 800, 814, 822, 830, 838, 846, 854, 862, 870, 878, 886,
]

export const AUFTRAGSSUMME_IDS = [
  558, 561, 567, 570, 662, 665, 668, 671, 675, 698, 755, 812, 897,
]

export const AUSFUHRUNG_IDS = [
  557, 560, 566, 569, 661, 664, 667, 670, 674, 697, 754, 811, 896,
]

export const AUSGEFUHRTDURCH_IDS = [673]

export const BESCHREIBUNG1_IDS = [
  786, 797, 805, 819, 827, 835, 843, 851, 859, 867, 875, 883, 891,
]

export const BESCHREIBUNG2_IDS = [
  788, 795, 803, 817, 825, 833, 841, 849, 857, 865, 873, 881, 889,
]

export const LEADTEXT_IDS = [
  785, 798, 806, 820, 828, 836, 844, 852, 860, 868, 876, 884, 892,
]

export const PROJEKTLEITER_IDS = [
  559, 562, 568, 571, 663, 666, 669, 672, 676, 699, 756, 813, 898,
]

export const REALISATION_IDS = [682]

export const TITELABSCHNITT1_IDS = [
  784, 799, 807, 821, 829, 837, 845, 853, 861, 869, 877, 885, 893,
]

export const TITELABSCHNITT2_IDS = [
  787, 796, 804, 818, 826, 834, 842, 850, 858, 866, 874, 882, 890,
]

export const ZITAT_IDS = [
  789, 794, 802, 816, 824, 832, 840, 848, 856, 864, 872, 880, 888,
]

export const ZITATLEGENDE_IDS = [
  790, 793, 801, 815, 823, 831, 839, 847, 855, 863, 871, 879, 887,
]
